/**
 * The three release-wide actions that are not component deploys, plus the merge-back
 * status they share a section with.
 *
 * They live together below the component table because they are the same shape of
 * thing: idempotent, release-wide, and each one a precondition of closing rather than
 * a step in a sequence. Announce is the one an operator will look for — it is what
 * tells thirty people the release happened — so it comes first.
 *
 * The knowledge publish is offered only when the release changes `docs/`. The host
 * would not refuse it otherwise — the publish diffs against GCS by checksum and is
 * harmless with nothing to send — but a card for an action with nothing to do is one an
 * operator learns to ignore, and then ignores on the release where it mattered. When it
 * is not offered the space says why, so its absence reads as a decision rather than a
 * missing feature.
 */
import { Alert, Button, Card, CardContent, Grid, Stack, Tooltip, Typography } from '@mui/material';
import {
    CONNECTOR_DOWNLOAD_COMPONENT,
    INSTRUCTION_KIND,
    componentAnnounceNarration,
    prepareStepLegality,
    type DeployComponentRelease,
    type Phase,
    type PrepareStep,
    type ReleaseDoc,
} from '@finvari/deployer';
import { useInstructionAction } from '../hooks';
import { ActionCard } from './ActionCard';
import { ActivityAlert } from './ActivityAlert';

/** The one `prepare.step` this page can issue; named once so the param and the gate agree. */
const MERGE_BACK_STEP: PrepareStep = 'mergeBack';

export function AuxiliarySection({ release, phase }: { release: ReleaseDoc; phase: Phase }) {
    return (
        <Stack spacing={1.5}>
            <Stack spacing={0.5}>
                <Typography variant="h6" component="h2">
                    Announce and publish
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
                    Everything a release does besides deploying components. Each is idempotent — running one twice does
                    the same thing once — and the release cannot close unannounced without a reason.
                </Typography>
            </Stack>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <ActionCard
                        host="release"
                        kind={INSTRUCTION_KIND.deployAnnounce}
                        release={release.version}
                        disabledReason={
                            release.announcement?.postedAt
                                ? 'Already posted to Slack.'
                                : release.announcement?.json
                                  ? undefined
                                  : 'This release has no announcement in the checkout. It is DVC content behind a tracked pointer, so sync the host — if it is still missing after that, the prepare host published none.'
                        }
                        emphasis={!release.announcement?.postedAt}
                    />
                </Grid>
                {/*
                  * One card per connector or app release riding this one. They sit
                  * beside the release's own announcement rather than under it
                  * because they are the same action with a different subject, and
                  * because the release cannot close while one of them is unposted —
                  * a card an operator has to go looking for is a close they will
                  * override instead.
                  */}
                {(release.componentReleases ?? []).map((componentRelease) => (
                    <Grid key={`${componentRelease.component}/${componentRelease.version}`} size={{ xs: 12, md: 6 }}>
                        <ComponentAnnounceCard release={release} componentRelease={componentRelease} />
                    </Grid>
                ))}
                <Grid size={{ xs: 12, md: 6 }}>
                    <ActionCard
                        host="release"
                        kind={INSTRUCTION_KIND.deployPublishFactoryArtifacts}
                        release={release.version}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    {release.docsChanged ? (
                        <ActionCard
                            host="release"
                            kind={INSTRUCTION_KIND.deployPublishKnowledge}
                            release={release.version}
                        />
                    ) : (
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Stack spacing={1}>
                                    <Typography variant="h6">Knowledge base</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        This release changes nothing under <code>docs/</code>, so there is nothing to
                                        publish and no card to press. Said rather than left blank, because the publish is
                                        part of a release and its absence should look deliberate.
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <MergeBackCard release={release} phase={phase} />
                </Grid>
            </Grid>
        </Stack>
    );
}

/**
 * The Announce card for one connector or app release riding this one.
 *
 * Its own card rather than a second use of the release's, because the
 * preconditions differ and the difference matters: the connector announcement
 * links to an installer that is DVC content, and it is the
 * `connector-download` deploy that puts that file in front of customers. The
 * host refuses the post until then; the card says so first, with the component
 * named, so the operator's next click is the deploy rather than a retry.
 */
function ComponentAnnounceCard({
    release,
    componentRelease,
}: {
    release: ReleaseDoc;
    componentRelease: DeployComponentRelease;
}) {
    const connectorDeployed = release.plan?.[CONNECTOR_DOWNLOAD_COMPONENT] !== undefined;
    const disabledReason = componentRelease.announcement?.postedAt
        ? 'Already posted to Slack.'
        : !componentRelease.announcement?.json
          ? 'This component release has no announcement in the checkout. It is DVC content behind a tracked pointer, so sync the host — if it is still missing after that, the prepare host drafted none.'
          : componentRelease.component === 'connector' && !connectorDeployed
            ? `${CONNECTOR_DOWNLOAD_COMPONENT} is not in this release's plan, so the installer this announcement links to is not being deployed. Check the change set before posting.`
            : undefined;

    return (
        <ActionCard
            host="release"
            kind={INSTRUCTION_KIND.deployAnnounce}
            params={{ component: componentRelease.component, version: componentRelease.version }}
            release={release.version}
            narration={componentAnnounceNarration(componentRelease)}
            disabledReason={disabledReason}
            emphasis={!componentRelease.announcement?.postedAt}
            // On this component's own instructions: without it the card would report
            // the outcome of whichever announce ran last, including the server's.
            match={(row) => (row.params as { component?: string } | undefined)?.component === componentRelease.component}
        />
    );
}

/**
 * Merge-back: the release branch has to come back to `main` or the next release is
 * cut without it. The status is observed here in git, but the *work* belongs to the
 * prepare host, so this is the one card on the deploying page that addresses the
 * other machine — the button issues `prepare.step mergeBack` against it.
 *
 * That cross-host button is worth the oddity. Merge-back runs automatically at the
 * end of publish and only lands here when it failed, which is exactly when an
 * operator has nothing else to press: leaving the card as a statement sent both
 * v1.1261 and v1.1262 to an SSH session. It sits with the other close preconditions
 * because someone looking at a blocked close needs to know whether to retry it,
 * wait, or acknowledge it.
 */
function MergeBackCard({ release, phase }: { release: ReleaseDoc; phase: Phase }) {
    const observed = release.mergeBackObserved;
    const { run, sending, error, latest, activity, disabledReason } = useInstructionAction(
        'prepare',
        INSTRUCTION_KIND.prepareStep,
        {
            params: { step: MERGE_BACK_STEP },
            release: release.version,
            disabledReason: prepareStepLegality(MERGE_BACK_STEP, phase),
            match: (row) => (row.params as { step?: string } | undefined)?.step === MERGE_BACK_STEP,
        },
    );

    const button = (
        <Button variant="outlined" size="small" onClick={run} disabled={sending || !!disabledReason}>
            {sending ? 'Sending…' : 'Run merge-back'}
        </Button>
    );

    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Stack spacing={1}>
                    <Typography variant="h6">Merge-back to main</Typography>
                    {observed ? (
                        <Alert severity="success">
                            <code>origin/main</code> contains {release.version}. The next release will be cut on top of
                            it.
                        </Alert>
                    ) : (
                        <>
                            <Alert severity="warning">
                                <code>origin/main</code> does not contain {release.version} yet, so a release cut now
                                would leave it out. Run it below — it is the prepare host's <strong>mergeBack</strong>{' '}
                                step, so it waits if that host is off — or acknowledge it on the close card.
                            </Alert>
                            {latest && activity && (
                                <ActivityAlert host="prepare" row={latest} line={activity} variant="inline" />
                            )}
                            {error && (
                                <Typography variant="body2" color="error">
                                    {error}
                                </Typography>
                            )}
                            {disabledReason ? <Tooltip title={disabledReason}><span>{button}</span></Tooltip> : button}
                        </>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        Re-derived from git at every sync and after every instruction, so this reflects the remote rather
                        than what anything here recorded.
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
}
